import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {apiCall, hideLoader, showLoader} from '../../utils';

import {
  Project,
  ProjectRevision,
  PublishAccess,
  PublishAccessListDTO,
  PublishAccessSaveDTO,
} from '@bumpy/i18n_common';

type Props = {
  project: Project;
  revision: ProjectRevision;
  open: boolean;
  onClose: () => void;
};

type EditInfo = {
  index: number;
  item?: PublishAccess;
};

export const PublishAccessDialog: React.FC<Props> = (props) => {
  const [list, setList] = React.useState<PublishAccess[]>([]);
  const [editInfo, setEditInfo] = React.useState<EditInfo>();

  React.useEffect(() => {
    if (!props.open) {
      return;
    }
    apiCall<PublishAccessListDTO>({
      method: 'GET',
      url: `/projects/${props.project.uuid}/revisions/${props.revision.uuid}/publish/access`,
      secure: true,
    }).then(({data}) => {
      setList(data.list);
    });
  }, [props.open, props.project.uuid, props.revision.uuid]);

  function onNewClick(): void {
    setList([
      {
        auth: false,
        email: '',
        lang: 'en',
        phone: '',
      },
      ...list,
    ]);
    setEditInfo({index: 0});
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={props.open} onClose={props.onClose}>
      <DialogTitle>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
        >
          <Typography>Publish access management</Typography>
          {!editInfo && (
            <IconButton onClick={onNewClick}>
              <AddIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <TableContainer sx={{maxHeight: 700}}>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Lang</TableCell>
                <TableCell>Device</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((item, index) => {
                if (index === editInfo?.index) {
                  function onChange(diff: Partial<PublishAccess>): void {
                    list[index] = {
                      ...item,
                      ...diff,
                    };
                    setList([...list]);
                  }

                  function onSave(): void {
                    showLoader('Saving...');
                    const data: PublishAccessSaveDTO = {
                      old: editInfo?.item,
                      new: item,
                    };
                    apiCall({
                      method: 'POST',
                      url: `/projects/${props.project.uuid}/revisions/${props.revision.uuid}/publish/access`,
                      secure: true,
                      data,
                    })
                      .then(() => {
                        setEditInfo(undefined);
                      })
                      .finally(hideLoader);
                  }

                  return (
                    <TableRow key={`edit_${index}`}>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.email}
                          size="small"
                          label="Email"
                          margin="none"
                          onChange={(event) => {
                            onChange({email: event.target.value});
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.phone}
                          size="small"
                          label="Phone"
                          margin="none"
                          onChange={(event) => {
                            onChange({phone: event.target.value});
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          value={item.lang}
                          variant="standard"
                          onChange={(event) => {
                            onChange({lang: event.target.value});
                          }}
                        >
                          {Object.keys(props.revision.translations).map(
                            (lang) => (
                              <MenuItem key={lang} value={lang}>
                                {lang}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        <IconButton onClick={onSave}>
                          <SaveIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }

                function onEdit(): void {
                  setEditInfo({index, item});
                }

                function onDelete(): void {
                  showLoader('Deleting...');
                  apiCall({
                    method: 'DELETE',
                    url: `/projects/${props.project.uuid}/revisions/${props.revision.uuid}/publish/access`,
                    secure: true,
                    data: item,
                  })
                    .then(() => {
                      const newList = [...list];
                      newList.splice(index, 1);
                      setList(newList);
                    })
                    .finally(hideLoader);
                }

                return (
                  <TableRow key={`static_${index}`}>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.phone}</TableCell>
                    <TableCell>{item.lang}</TableCell>
                    <TableCell>{item.auth ? 'Y' : 'N'}</TableCell>
                    <TableCell>
                      <Stack direction="row">
                        <IconButton onClick={onEdit}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={onDelete}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};
